import Contingent from '../models/contingent';
import { Trans } from 'react-i18next';
import React from 'react';
import { Divider, Label, Icon } from 'semantic-ui-react';
import { Tooltip, GraphLine } from '@weezevent/nacre';

/**
 * Returns the render of a single contingent row
 * @param {Object} contingent - Contingent to render
 * @param {Object} inventory
 * @param {Object} organization
 * @param {Object} event
 * @returns {React.Component} Rendererd contingent
 */
function renderContingent(item, inventory, organization, event) {
    if (!item) {
        return null;
    }

    let allQuota = item.available + item.booked + item.bookingInProgress;
    let isInventorySocialDistancing = inventory.social_distancing_x + inventory.social_distancing_y + inventory.social_distancing_z > 0;
    return (
        <React.Fragment key={item.uuid}>
            <li className="contingent vertical" data-id={item.uuid}>
                <div className="info">
                    <Icon name="circle" style={{ color: item.color }} />
                    <div className="name listed">
                        {item.name} {item.bookingInProgress ? <Label>+{item.bookingInProgress}</Label> : null}
                    </div>
                </div>

                <div className="contingent-list-capacity">
                    <Trans
                        i18nKey="tabularZoneCapacity"
                        values={{
                            capacity: item.all
                        }}
                    />
                    {isInventorySocialDistancing ? (
                        <Tooltip
                            morePlaceholder={<Trans i18nKey="ticket.availability.tooltip_social_distancing_placeholder_link" />}
                            moreURL={`/ticket/O${organization.id}/E${event.id}/manager/attendees`}
                            position={'bottom'}
                            title={
                                <div>
                                    <div>
                                        <Trans i18nKey="ticket.availability.tooltip_social_distancing_text_status" />
                                    </div>
                                    <div>
                                        <Trans i18nKey="ticket.availability.tooltip_social_distancing_text_capacity_info" />
                                    </div>
                                    <div>
                                        <Trans i18nKey="ticket.availability.tooltip_social_distancing_text_link_info" />
                                    </div>
                                    <br />
                                </div>
                            }
                        >
                            <Icon className="icon-help" name="question" />
                        </Tooltip>
                    ) : null}
                </div>

                <div>{!isNaN(item.all) ? `${item.booked}  ventes / ${allQuota} quota` : 'N/A'}</div>
                <GraphLine max={+allQuota} tooltip={false} value={+item.booked} />
            </li>
            <Divider />
        </React.Fragment>
    );
}

/**
 * Computes contingent sales and returns rendered contingent sales list item
 * @param {Object} params - Parameters object
 * @param {Object} params.availability - Needed parameters for computation
 * @param {Contingent[]} params.mainContingents - List of all contingents
 * @returns {React.Component[]} List of Contingent Sale list items
 */
export function computeContingentSales({ availability, mainContingents, inventory, organization, event }) {
    let contingents = Contingent.sortByName(mainContingents);
    let acc = {};
    availability.children.forEach(zone => {
        zone.categories.forEach(category => {
            category.contingents.forEach(contingent => {
                if (!acc[contingent.uuid]) {
                    acc[contingent.uuid] = {
                        all: 0,
                        available: 0,
                        booked: 0,
                        bookingInProgress: 0
                    };
                }
                acc[contingent.uuid].all += contingent.all;
                acc[contingent.uuid].available += contingent.available;
                acc[contingent.uuid].booked += contingent.booked;
                acc[contingent.uuid].bookingInProgress += contingent.booking_in_progress;
                return null;
            });
            return null;
        });
        return null;
    });

    availability.seats.forEach(seat => {
        if (seat.contingent) {
            if (!acc[seat.contingent]) {
                acc[seat.contingent] = {
                    all: 0,
                    available: 0,
                    booked: 0,
                    bookingInProgress: 0
                };
            }
            acc[seat.contingent].all += 1;
            if (seat.booking_state === 'AVAILABLE') {
                acc[seat.contingent].available += 1;
            }
            if (seat.booking_state === 'BOOKED') {
                acc[seat.contingent].booked += 1;
            }
            if (seat.booking_state === 'BOOKING_IN_PROGRESS') {
                acc[seat.contingent].bookingInProgress += 1;
            }
        }
        return null;
    });

    contingents = contingents
        .map(contingent => {
            if (!acc[contingent.uuid]) {
                return null;
            }
            let renderedContingent = contingent.copy();
            renderedContingent.booked = acc[renderedContingent.uuid].booked;
            renderedContingent.bookingInProgress = acc[renderedContingent.uuid].bookingInProgress;
            renderedContingent.all = acc[renderedContingent.uuid].all;
            renderedContingent.available = acc[renderedContingent.uuid].available;
            return renderContingent(renderedContingent, inventory, organization, event);
        })
        .filter(contingent => contingent);
    return contingents;
}

export function computeCategorySales({ availability, categories, inventory, organization, event }) {
    let acc = {};
    availability.children.forEach(zone => {
        zone.categories.forEach(category => {
            if (!acc[category.uuid]) {
                acc[category.uuid] = {
                    all: 0,
                    available: 0,
                    booked: 0,
                    bookingInProgress: 0
                };
            }
            acc[category.uuid].all += category.all;
            acc[category.uuid].available += category.available;
            acc[category.uuid].booked += category.booked;
            acc[category.uuid].bookingInProgress += category.booking_in_progress;
            return null;
        });
        return null;
    });

    availability.seats.forEach(seat => {
        if (seat.category) {
            if (!acc[seat.category]) {
                acc[seat.category] = {
                    all: 0,
                    available: 0,
                    booked: 0,
                    bookingInProgress: 0
                };
            }
            acc[seat.category].all += 1;
            if (seat.booking_state === 'AVAILABLE') {
                acc[seat.category].available += 1;
            }
            if (seat.booking_state === 'BOOKED') {
                acc[seat.category].booked += 1;
            }
            if (seat.booking_state === 'BOOKING_IN_PROGRESS') {
                acc[seat.category].bookingInProgress += 1;
            }
        }
        return null;
    });

    return categories
        .map(category => {
            if (!acc[category.uuid]) {
                return null;
            }
            let renderedCategory = category.copy();
            renderedCategory.booked = acc[renderedCategory.uuid].booked;
            renderedCategory.bookingInProgress = acc[renderedCategory.uuid].bookingInProgress;
            renderedCategory.all = acc[renderedCategory.uuid].all;
            renderedCategory.available = acc[renderedCategory.uuid].available;
            return renderContingent(renderedCategory, inventory, organization, event);
        })
        .filter(e => e);
}
